import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Calculator, Send, TrendingUp, TrendingDown, Copy, UserPlus, Bed, Paperclip, FileText, ExternalLink, ZoomIn } from "lucide-react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";

/**
 * Weekend Cohort Organizer — click a cohort card in Trips.jsx to open this.
 * Handles cost breakdown: tourists roster (pre-filled from cohort), meals,
 * monuments, hotels, taxi, guides, water/snacks, staff expenses. Live grand
 * total. Founder + BDOM can save (no approval per user preference).
 */
export default function CohortOrganizerModal({ cohort, onClose }) {
  const cohortKey = `${cohort.start_date || "nodate"}__${(cohort.destinations || "").replace(/\s+/g,"_")}`;
  const [days, setDays] = useState(1);
  const [tourists, setTourists] = useState([]);
  const [meals, setMeals] = useState({ breakfast: 0, lunch: 0, dinner: 0 });
  const [monuments, setMonuments] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [taxi, setTaxi] = useState([]);
  const [guides, setGuides] = useState([]);
  const [waterSnacks, setWaterSnacks] = useState(0);
  const [staffHotelRoom, setStaffHotelRoom] = useState(0);
  const [staffMealsPerDay, setStaffMealsPerDay] = useState(600);
  const [notes, setNotes] = useState("");
  const [maxPax, setMaxPax] = useState(0);
  const [minMarginPct, setMinMarginPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRows, setPickerRows] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupDate, setDupDate] = useState("");
  const [dupDest, setDupDest] = useState(cohort.destinations || "");
  // Display currency toggle — the sheet is ALWAYS persisted in INR (single
  // source of truth for the weekly digest). This just re-renders every
  // amount at the current FX so foreign partners see native numbers.
  const [displayCcy, setDisplayCcy] = useState("INR");
  const [fxRates, setFxRates] = useState({ INR: 1 });
  // Registry-backed dropdowns — pulls from /api/hotels /taxis /guides so
  // the Founder picks from the vendors they've curated in Registries
  // instead of re-typing names for every cohort.
  const [lightbox, setLightbox] = useState(null);  // { url, kind, name } — opens overlay preview

  const [hotelReg, setHotelReg] = useState([]);
  const [taxiReg, setTaxiReg] = useState([]);
  const [guideReg, setGuideReg] = useState([]);
  const [monumentReg, setMonumentReg] = useState([]);
  // Registries are city-agnostic — load them once on mount.
  useEffect(() => {
    Promise.all([
      api.get("/hotels").catch(() => ({ data: [] })),
      api.get("/taxis").catch(() => ({ data: [] })),
      api.get("/guides").catch(() => ({ data: [] })),
    ]).then(([h, t, g]) => {
      setHotelReg(h.data || []);
      setTaxiReg(t.data || []);
      setGuideReg(g.data || []);
    });
  }, []);
  // Monuments are scoped by cohort city — refetch when the city
  // changes so switching between a Jaipur and a Udaipur cohort
  // pulls the right entry-price registry.
  useEffect(() => {
    const q = cohort?.city ? `?city=${encodeURIComponent(cohort.city)}` : "";
    api.get(`/monuments${q}`)
      .then(({ data }) => setMonumentReg(data || []))
      .catch(() => setMonumentReg([]));
  }, [cohort?.city]);
  useEffect(() => {
    if (displayCcy === "INR" || fxRates[displayCcy]) return;
    api.get(`/fx-rate?currency=${displayCcy}`).then(({ data }) => {
      setFxRates(r => ({ ...r, [displayCcy]: data.rate || 1 }));
    }).catch(() => setFxRates(r => ({ ...r, [displayCcy]: 1 })));
  }, [displayCcy, fxRates]);
  const fx = fxRates[displayCcy] || 1;         // 1 unit of displayCcy in INR
  const cSym = displayCcy === "INR" ? "₹" : displayCcy === "USD" ? "$" : displayCcy === "EUR" ? "€" : "£";
  const fmt = (n) => `${cSym}${(Number(n || 0) / fx).toLocaleString(undefined, { maximumFractionDigits: displayCcy === "INR" ? 0 : 2 })}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/weekend-cohort-organizer/${cohortKey}`);
        setDays(data.days || 1);
        // Auto-seed tourists from the cohort roster the first time this
        // organizer is opened. Once saved, the persisted list wins.
        setTourists(data.tourists?.length ? data.tourists : (cohort.participants || []).map(p => ({
          id: p.id, name: p.customer, partner: p.partner_name || "", gender: p.gender || "",
        })));
        setMeals(data.meals || { breakfast: 0, lunch: 0, dinner: 0 });
        setMonuments(data.monuments || []);
        setHotels(data.hotels || []);
        setTaxi(data.taxi || []);
        setGuides(data.guides || []);
        setWaterSnacks(data.water_snacks || 0);
        setStaffHotelRoom(data.staff_hotel_room || 0);
        setStaffMealsPerDay(data.staff_meals_per_day ?? 600);
        setNotes(data.notes || "");
        setMaxPax(data.max_pax || 0);
        setMinMarginPct(data.min_margin_pct || 0);
      } catch { /* first-open — leave defaults */ }
    })();
  }, [cohortKey, cohort]);

  const pax = tourists.length;
  // Gender split — quick M/F/Other roll-up so GEHM eyeballs the room mix.
  // Reads from the currently edited `tourists` list so it stays live as
  // gender selects change (not the initial cohort snapshot).
  const genderSplit = useMemo(() => {
    const s = { male: 0, female: 0, other: 0, unknown: 0 };
    tourists.forEach(t => {
      const g = (t.gender || "").toLowerCase();
      if (g === "male") s.male++;
      else if (g === "female") s.female++;
      else if (g === "" || g === "unknown" || g === "prefer_not_to_say") s.unknown++;
      else s.other++;
    });
    return s;
  }, [tourists]);
  // Room-mix suggestion — pack same-gender guests into shared rooms of 3
  // (triple) → 2 (double), leftover goes to a private single. "Other" +
  // "unknown" always default to private singles so nobody's outed by the
  // room allocation. Pure client-side math so GEHM sees a live suggestion
  // as they toggle genders in the roster above.
  const roomSuggestion = useMemo(() => {
    const pack = (n) => {
      const triples = Math.floor(n / 3);
      const rem = n % 3;
      const doubles = rem === 2 ? 1 : 0;
      const singles = rem === 1 ? 1 : 0;
      return { triples, doubles, singles, rooms: triples + doubles + singles };
    };
    const m = pack(genderSplit.male);
    const f = pack(genderSplit.female);
    const singles = genderSplit.other + genderSplit.unknown; // private each
    const totalRooms = m.rooms + f.rooms + singles;
    return { m, f, singles, totalRooms };
  }, [genderSplit]);
  // Revenue attribution — the parent Trips.jsx passes `cohort.participants[]`
  // with per-booking `revenue` (sale price → tour_price fallback). We sum
  // that to produce the cohort's total revenue for the profitability strip.
  // If nothing is priced yet, revenue = 0 and the margin turns red — a
  // useful "you forgot to price this cohort" signal for Founder.
  const revenue = useMemo(() =>
    (cohort.participants || []).reduce((s, p) => s + (Number(p.revenue) || 0), 0),
  [cohort]);
  const totals = useMemo(() => {
    const mealsPerPersonPerDay = Number(meals.breakfast || 0) + Number(meals.lunch || 0) + Number(meals.dinner || 0);
    const mealsTotal = mealsPerPersonPerDay * pax * days;
    const monumentsTotal = monuments.reduce((s, m) => s + Number(m.price || 0) * pax, 0);
    const hotelsTotal = hotels.reduce((s, h) => s + Number(h.price_per_night || 0) * Number(h.nights || 1), 0);
    const taxiTotal = taxi.reduce((s, t) => s + Number(t.amount || 0), 0);
    const guidesTotal = guides.reduce((s, g) => s + Number(g.fee || 0), 0);
    const waterTotal = Number(waterSnacks || 0) * pax * days;
    const staffTotal = Number(staffHotelRoom || 0) + Number(staffMealsPerDay || 0) * days;
    const grand = mealsTotal + monumentsTotal + hotelsTotal + taxiTotal + guidesTotal + waterTotal + staffTotal;
    const margin = revenue - grand;
    const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
    return { mealsTotal, monumentsTotal, hotelsTotal, taxiTotal, guidesTotal, waterTotal, staffTotal, grand, margin, marginPct };
  }, [meals, monuments, hotels, taxi, guides, waterSnacks, staffHotelRoom, staffMealsPerDay, pax, days, revenue]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/weekend-cohort-organizer/${cohortKey}`, {
        days, tourists, meals, monuments, hotels, taxi, guides,
        water_snacks: waterSnacks, staff_hotel_room: staffHotelRoom,
        staff_meals_per_day: staffMealsPerDay, notes, max_pax: maxPax,
        min_margin_pct: minMarginPct, revenue_inr: revenue,
        grand_total: totals.grand, currency: "INR",
      });
      toast.success(`Saved · Grand total ₹${totals.grand.toLocaleString()}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const [discarding, setDiscarding] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState("");
  const discard = async () => {
    setDiscarding(true);
    try {
      await api.delete(`/weekend-cohort-organizer/${cohortKey}`, { params: { reason: discardReason.trim() } });
      toast.success("Cost sheet discarded · restore from Recycle Bin if this was a mistake");
      setDiscardOpen(false);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discard failed");
    } finally { setDiscarding(false); }
  };

  // Registry Quick-Add — Founder/BDOM can promote a one-off vendor name
  // straight into the shared Registry from inside the modal so the next
  // cohort finds it in the dropdown without a separate settings trip.
  const quickAddToRegistry = async (kind, name, extra = {}) => {
    const clean = (name || "").trim();
    if (!clean) return;
    try {
      const { data } = await api.post(`/${kind}`, { name: clean, ...extra });
      if (kind === "hotels") setHotelReg(reg => [...reg, data]);
      else if (kind === "taxis") setTaxiReg(reg => [...reg, data]);
      else if (kind === "guides") setGuideReg(reg => [...reg, data]);
      else if (kind === "monuments") setMonumentReg(reg => [...reg, data]);
      toast.success(`Added "${clean}" to ${kind.charAt(0).toUpperCase() + kind.slice(1)} Registry`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not save to ${kind} registry`);
    }
  };

  const share = async () => {
    // Persist first so the emailed PDF reflects the latest tweaks.
    setSharing(true);
    try {
      await api.post(`/weekend-cohort-organizer/${cohortKey}`, {
        days, tourists, meals, monuments, hotels, taxi, guides,
        water_snacks: waterSnacks, staff_hotel_room: staffHotelRoom,
        staff_meals_per_day: staffMealsPerDay, notes, max_pax: maxPax,
        min_margin_pct: minMarginPct, revenue_inr: revenue,
        grand_total: totals.grand, currency: "INR",
      });
      const { data } = await api.post(`/weekend-cohort-organizer/${cohortKey}/email`, { extra_recipients: [] });
      toast.success(`Emailed to ${data.sent_to} recipient(s) · PDF ready`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
    finally { setSharing(false); }
  };

  const downloadPdf = () => {
    // Direct GET with auth token so browser can save the file straight away.
    const token = getToken();
    const url = `${API}/weekend-cohort-organizer/${cohortKey}/pdf?auth=${token}`;
    window.open(url, "_blank");
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerRows(null);
    try {
      const { data } = await api.get(`/weekend-cohort-picker/${cohortKey}`);
      setPickerRows(data || []);
    } catch { setPickerRows([]); }
  };

  const addFromPicker = (r) => {
    setTourists(ts => [...ts, { id: r.id, name: r.customer, partner: r.partner || "", gender: r.gender || "" }]);
    setPickerRows(rows => rows.filter(x => x.id !== r.id));
  };

  // ------------------------------------------------------------------
  // Booking-confirmation attachments — Founder can attach hotel booking
  // vouchers or taxi bills to each hotel/taxi row before OR after the
  // tour. Uploads go through the shared /uploads endpoint (Object
  // Storage) and the returned file metadata is stored on the row so it
  // rides along with the cost sheet.
  // ------------------------------------------------------------------
  const uploadAttachmentTo = async (rowKind, index, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15 MB)"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const meta = {
        id: data.id,
        name: file.name,
        public_url: data.public_url || "",
        kind: data.kind || file.type || "file",
        size: file.size,
        uploaded_at: new Date().toISOString(),
      };
      const push = (list, setList) => setList(list.map((x, j) => j === index ? { ...x, attachments: [...(x.attachments || []), meta] } : x));
      if (rowKind === "hotel") push(hotels, setHotels);
      else if (rowKind === "taxi") push(taxi, setTaxi);
      toast.success(`Attached ${file.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const removeAttachment = (rowKind, index, attId) => {
    const strip = (list, setList) => setList(list.map((x, j) => j === index ? { ...x, attachments: (x.attachments || []).filter(a => a.id !== attId) } : x));
    if (rowKind === "hotel") strip(hotels, setHotels);
    else if (rowKind === "taxi") strip(taxi, setTaxi);
  };

  const duplicate = async () => {
    // Persist current sheet first so the copy reflects the latest tweaks.
    const targetKey = `${dupDate}__${(dupDest || "").replace(/\s+/g, "_")}`;
    if (!dupDate || !dupDest) { toast.error("Pick a date and destination first"); return; }
    if (targetKey === cohortKey) { toast.error("Target must differ from source"); return; }
    try {
      await api.post(`/weekend-cohort-organizer/${cohortKey}`, {
        days, tourists, meals, monuments, hotels, taxi, guides,
        water_snacks: waterSnacks, staff_hotel_room: staffHotelRoom,
        staff_meals_per_day: staffMealsPerDay, notes, max_pax: maxPax,
        min_margin_pct: minMarginPct, revenue_inr: revenue,
        grand_total: totals.grand, currency: "INR",
      });
      await api.post(`/weekend-cohort-organizer/${cohortKey}/duplicate`, { to_cohort_key: targetKey });
      toast.success(`Duplicated to ${dupDest} · ${dupDate}`);
      setDupOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const numInput = (val, onChange, placeholder = "0") => (
    <input type="number" min="0" step="0.01" value={val} onChange={e => onChange(parseFloat(e.target.value) || 0)}
           placeholder={placeholder}
           className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm w-24"/>
  );
  const txtInput = (val, onChange, ph, cls = "flex-1") => (
    <input value={val} onChange={e => onChange(e.target.value)} placeholder={ph}
           className={`${cls} px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm min-w-[100px]`}/>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="cohort-organizer-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3 sticky top-0 bg-white rounded-t-lg z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Weekend Cohort Organizer</div>
            {/* Prefer the specific tour title (e.g. "Indian Golden Triangle" /
                "India Ranthambhore Tiger Safari") so operators see the trip
                identity front-and-centre. Fall back to weekend_trips (the
                legacy freeform field) and then to the destination + date so
                older cohorts without a title still render. */}
            <h3 className="font-display text-xl font-semibold">
              {cohort.tour_title || cohort.weekend_trips || cohort.destinations || "Cohort"}
            </h3>
            <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
              📍 {cohort.destinations || "—"} · {cohort.start_date || "—"}
            </div>
            <div className="text-xs text-[color:var(--aii-muted)]">{pax} tourist{pax===1?"":"s"} · {days} day{days===1?"":"s"} · grand total <b className="text-brand">₹{totals.grand.toLocaleString()}</b></div>
          </div>
          <button onClick={onClose} className="p-2 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-5">
          {/* Days */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Days</label>
            <input type="number" min="1" value={days} onChange={e=>setDays(parseInt(e.target.value)||1)}
                   data-testid="cohort-days"
                   className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm w-20"/>
          </div>

          {/* Tourists roster — pax count drives every per-pax cost line
              below (meals, monuments, water). Founder can also add manual
              guests (e.g. a spouse tagging along) or remove no-shows. */}
          <section>
            <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Tourists ({pax}) — count drives meals &amp; per-pax costs</div>
              {/* Gender split chip — helps GEHM plan rooms + drivers plan hotel check-ins. */}
              {pax > 0 && (
                <div className="text-[10px] flex items-center gap-1" data-testid="cohort-gender-split">
                  <span className="uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold">Gender split</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">M {genderSplit.male}</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700 font-semibold">F {genderSplit.female}</span>
                  {genderSplit.other > 0 && <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">Other {genderSplit.other}</span>}
                  {genderSplit.unknown > 0 && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">? {genderSplit.unknown}</span>}
                </div>
              )}
            </div>
            {/* Room-mix suggestion — one-liner GEHM can copy straight into a
                hotel-block request. Updates live as gender selects change. */}
            {pax > 0 && (
              <div className="text-[11px] mb-2 flex items-start gap-2 bg-[color:var(--aii-green-tint)] border border-[color:var(--aii-border)] rounded-md px-2 py-1.5"
                   data-testid="cohort-room-suggestion">
                <Bed size={13} className="text-brand mt-0.5"/>
                <div>
                  <b className="text-brand">Room mix suggestion:</b>{" "}
                  <span className="text-[color:var(--aii-text-2)]">
                    {(() => {
                      const bits = [];
                      const push = (label, {triples, doubles, singles}) => {
                        const parts = [];
                        if (triples) parts.push(`${triples} triple`);
                        if (doubles) parts.push(`${doubles} double`);
                        if (singles) parts.push(`${singles} single`);
                        if (parts.length) bits.push(`${parts.join(" + ")} (${label})`);
                      };
                      push("M", roomSuggestion.m);
                      push("F", roomSuggestion.f);
                      if (roomSuggestion.singles) bits.push(`${roomSuggestion.singles} private single (Other / unspecified)`);
                      return bits.length ? bits.join(" · ") : "add tourists to see mix";
                    })()}
                    {" — "}
                    <b className="text-brand">{roomSuggestion.totalRooms} room(s) total</b>
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-1" data-testid="cohort-tourists">
              {tourists.map((t, i) => (
                // Composite key: name (or empty slot marker) + position.
                // A pure index key was flagged for state-loss on
                // reorder — the name is stable across renders for a
                // given roster row so focus stays on the right input.
                <div key={`tourist-${t.name || "_"}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`cohort-tourist-${i}`}>
                  <span className="w-5 text-[color:var(--aii-muted)] text-xs">{i + 1}.</span>
                  <input value={t.name || ""}
                         onChange={e => setTourists(tourists.map((x, j) => j === i ? {...x, name: e.target.value} : x))}
                         placeholder="Tourist name"
                         className="font-medium flex-1 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
                  {(() => {
                    // Compact M/F/O pill mirrors the select value so GEHM
                    // can scan the roster at a glance without reading a
                    // dozen dropdowns.
                    const g = (t.gender || "").toLowerCase();
                    const cfg = g === "male" ? { l: "M", cls: "bg-blue-100 text-blue-700" }
                              : g === "female" ? { l: "F", cls: "bg-pink-100 text-pink-700" }
                              : (g && g !== "unknown" && g !== "prefer_not_to_say") ? { l: "O", cls: "bg-purple-100 text-purple-700" }
                              : null;
                    return cfg ? (
                      <span data-testid={`cohort-tourist-badge-${i}`} title={`Gender: ${g}`}
                        className={`inline-flex items-center justify-center text-[10px] font-bold w-5 h-5 rounded-full ${cfg.cls}`}>{cfg.l}</span>
                    ) : (
                      <span data-testid={`cohort-tourist-badge-${i}`} title="Gender not set"
                        className="inline-flex items-center justify-center text-[10px] font-bold w-5 h-5 rounded-full bg-slate-100 text-slate-400">?</span>
                    );
                  })()}
                  <select value={(t.gender || "").toLowerCase()}
                          onChange={e => setTourists(tourists.map((x, j) => j === i ? {...x, gender: e.target.value} : x))}
                          data-testid={`cohort-tourist-gender-${i}`}
                          className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white w-24"
                          title="Gender — helps GEHM plan rooms &amp; drivers plan hotel check-ins">
                    <option value="">Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                  <input value={t.partner || ""}
                         onChange={e => setTourists(tourists.map((x, j) => j === i ? {...x, partner: e.target.value} : x))}
                         placeholder="via partner (optional)"
                         className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white w-40"/>
                  <button type="button" onClick={() => setTourists(tourists.filter((_, j) => j !== i))}
                          className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={12}/></button>
                </div>
              ))}
              <button type="button"
                onClick={() => setTourists([...tourists, { id: `manual-${Date.now()}`, name: "", partner: "" }])}
                data-testid="cohort-add-tourist"
                className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1">
                <Plus size={11}/> Add tourist
              </button>
              <button type="button" onClick={openPicker} data-testid="cohort-pick-tourist"
                className="ml-2 text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1">
                <UserPlus size={11}/> Pull from current bookings
              </button>
              <label className="ml-2 text-[10px] uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold inline-flex items-center gap-1">
                Max pax
                <input type="number" min="0" value={maxPax}
                       onChange={e => setMaxPax(parseInt(e.target.value) || 0)}
                       data-testid="cohort-max-pax"
                       className="ml-1 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm w-20"/>
              </label>
              <label className="ml-2 text-[10px] uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold inline-flex items-center gap-1"
                     title="Show a red banner + email digest flag when margin drops below this pct">
                Min margin %
                <input type="number" min="0" max="100" value={minMarginPct}
                       onChange={e => setMinMarginPct(parseFloat(e.target.value) || 0)}
                       data-testid="cohort-min-margin"
                       className="ml-1 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm w-16"/>
              </label>
            </div>
          </section>

          {/* Meals */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Meals (₹ per person per meal · leave 0 if complimentary)</div>
            <div className="flex flex-wrap gap-3 items-center" data-testid="cohort-meals">
              <label className="text-xs flex items-center gap-1">Breakfast {numInput(meals.breakfast, v => setMeals({...meals, breakfast: v}))}</label>
              <label className="text-xs flex items-center gap-1">Lunch {numInput(meals.lunch, v => setMeals({...meals, lunch: v}))}</label>
              <label className="text-xs flex items-center gap-1">Dinner {numInput(meals.dinner, v => setMeals({...meals, dinner: v}))}</label>
              <span className="text-xs text-[color:var(--aii-muted)]">= ₹{totals.mealsTotal.toLocaleString()} ({pax} pax × {days} d)</span>
            </div>
          </section>

          {/* Monuments */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Monument tickets (× {pax} pax)</div>
            <div className="space-y-1">
              {monuments.map((m, i) => (
                <div key={`monument-${m.name || "_"}-${i}`} className="flex items-center gap-2" data-testid={`cohort-mon-${i}`}>
                  <select value={monumentReg.some(x => x.name === m.name) ? m.name : (m.name ? "__custom__" : "")}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === "__addreg__") setMonuments(monuments.map((x, j) => j === i ? {...x, name: "", _promote: true} : x));
                            else if (v === "__custom__") setMonuments(monuments.map((x, j) => j === i ? {...x, name: "", _promote: false} : x));
                            else {
                              const reg = monumentReg.find(r => r.name === v);
                              // Bulk-imported monuments only carry
                              // weekday_price/weekend_price fields — the
                              // legacy `price_per_person`/`price` check
                              // silently stamped 0 on every pick. Prefer
                              // weekend_price for this weekend cohort,
                              // then weekday_price, then legacy fields.
                              const regPrice = reg?.weekend_price ?? reg?.weekday_price ?? reg?.price_per_person ?? reg?.price;
                              setMonuments(monuments.map((x, j) => j === i ? {...x, name: v, price: Number(regPrice ?? x.price) || 0, _promote: false} : x));
                            }
                          }}
                          data-testid={`cohort-mon-select-${i}`}
                          className="flex-1 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                    <option value="">— Pick a monument from Registry ({monumentReg.length}) —</option>
                    {monumentReg.map(r => {
                      const p = r.weekend_price ?? r.weekday_price ?? r.price_per_person ?? r.price;
                      return <option key={r.id} value={r.name}>{r.name}{p ? ` · ₹${p}/pax` : ""}</option>;
                    })}
                    <option value="__custom__">✎ Custom / new monument…</option>
                    <option value="__addreg__">➕ Add new monument to Registry…</option>
                  </select>
                  {!monumentReg.some(x => x.name === m.name) && m.name !== "" && (
                    <input value={m.name || ""}
                           onChange={e => setMonuments(monuments.map((x, j) => j === i ? {...x, name: e.target.value} : x))}
                           onBlur={(e) => { if (m._promote) { quickAddToRegistry("monuments", e.target.value, { price_per_person: m.price }); setMonuments(monuments.map((x, j) => j === i ? {...x, _promote: false} : x)); } }}
                           placeholder={m._promote ? "New monument (auto-saves to Registry)" : "Custom monument"}
                           className={`flex-1 px-2 py-1 border rounded bg-white text-sm ${m._promote ? "border-brand" : "border-[color:var(--aii-border)]"}`}/>
                  )}
                  {numInput(m.price, v => setMonuments(monuments.map((x, j) => j === i ? {...x, price: v} : x)), "₹/pax")}
                  <span className="text-xs text-[color:var(--aii-muted)] w-24">= ₹{(Number(m.price||0) * pax).toLocaleString()}</span>
                  <button type="button" onClick={() => setMonuments(monuments.filter((_, j) => j !== i))} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={12}/></button>
                </div>
              ))}
              <button type="button" onClick={() => setMonuments([...monuments, { name: "", price: 0 }])}
                      data-testid="cohort-add-monument"
                      className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1"><Plus size={11}/> Add monument</button>
            </div>
          </section>

          {/* Hotels — Registry-backed dropdown + per-hotel booking confirmation attachments */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Hotels (attach each hotel&apos;s booking confirmation PDF)</div>
            <div className="space-y-2">
              {hotels.map((h, i) => (
                <div key={`hotel-${h.name || "_"}-${i}`} className="space-y-1" data-testid={`cohort-hotel-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select value={hotelReg.some(x => x.name === h.name) ? h.name : (h.name ? "__custom__" : "")}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === "__addreg__") setHotels(hotels.map((x, j) => j === i ? {...x, name: "", _promote: true} : x));
                              else if (v === "__custom__") setHotels(hotels.map((x, j) => j === i ? {...x, name: "", _promote: false} : x));
                              else {
                                const reg = hotelReg.find(r => r.name === v);
                                setHotels(hotels.map((x, j) => j === i ? {...x, name: v, price_per_night: reg?.price_per_night ?? x.price_per_night ?? 0, _promote: false} : x));
                              }
                            }}
                            data-testid={`cohort-hotel-select-${i}`}
                            className="flex-1 min-w-[200px] px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                      <option value="">— Pick a hotel from Registry ({hotelReg.length}) —</option>
                      {hotelReg.map(r => <option key={r.id} value={r.name}>{r.name}{r.extra ? ` · ${r.extra}` : ""}</option>)}
                      <option value="__custom__">✎ Custom / new hotel…</option>
                      <option value="__addreg__">➕ Add new hotel to Registry…</option>
                    </select>
                    {!hotelReg.some(x => x.name === h.name) && h.name !== "" && (
                      <input value={h.name || ""}
                             onChange={e => setHotels(hotels.map((x, j) => j === i ? {...x, name: e.target.value} : x))}
                             onBlur={(e) => { if (h._promote) { quickAddToRegistry("hotels", e.target.value); setHotels(hotels.map((x, j) => j === i ? {...x, _promote: false} : x)); } }}
                             placeholder={h._promote ? "New hotel (auto-saves to Registry)" : "Custom hotel"}
                             data-testid={`cohort-hotel-label-${i}`}
                             className={`flex-1 min-w-[160px] px-2 py-1 border rounded bg-white text-sm ${h._promote ? "border-brand" : "border-[color:var(--aii-border)]"}`}/>
                    )}
                    {numInput(h.price_per_night, v => setHotels(hotels.map((x, j) => j === i ? {...x, price_per_night: v} : x)), "₹/night")}
                    <input type="number" min="1" value={h.nights||1} onChange={e => setHotels(hotels.map((x, j) => j === i ? {...x, nights: parseInt(e.target.value)||1} : x))} className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm w-16" title="Nights"/>
                    <span className="text-xs text-[color:var(--aii-muted)] w-24">= ₹{(Number(h.price_per_night||0) * Number(h.nights||1)).toLocaleString()}</span>
                    <label title="Attach hotel booking confirmation PDF" data-testid={`cohort-hotel-attach-${i}`}
                           className="text-[color:var(--aii-muted)] hover:text-brand cursor-pointer p-1 flex items-center gap-1">
                      <Paperclip size={14}/>
                      <span className="text-[10px] hidden sm:inline">Booking PDF</span>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden"
                             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadAttachmentTo("hotel", i, f); }}/>
                    </label>
                    <button onClick={() => setHotels(hotels.filter((_, j) => j !== i))} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={12}/></button>
                  </div>
                  {(h.attachments || []).length > 0 && (
                    <div className="ml-4 flex flex-wrap gap-2 text-[10px]">
                      {(h.attachments || []).map(att => {
                        const isImg = (att.kind || "").startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(att.name || "");
                        const isPdf = (att.kind || "").includes("pdf") || /\.pdf$/i.test(att.name || "");
                        return (
                          <a key={att.id} href={att.public_url || "#"}
                             onClick={(e) => {
                               // Prefer in-app lightbox for images & PDFs so partners
                               // don't lose their place in the modal. Non-preview
                               // formats still fall through to the new-tab download.
                               const isImg = (att.kind || "").startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(att.name || "");
                               const isPdf = (att.kind || "").includes("pdf") || /\.pdf$/i.test(att.name || "");
                               if (att.public_url && (isImg || isPdf)) {
                                 e.preventDefault();
                                 setLightbox({ url: att.public_url, kind: isPdf ? "pdf" : "image", name: att.name });
                               }
                             }}
                             target="_blank" rel="noreferrer"
                             className="group flex items-center gap-2 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white hover:border-brand transition"
                             title={att.name}>
                            {isImg && att.public_url ? (
                              <img src={att.public_url} alt="" className="w-10 h-10 object-cover rounded" loading="lazy"/>
                            ) : isPdf ? (
                              <div className="w-10 h-10 rounded bg-red-50 border border-red-200 flex items-center justify-center">
                                <FileText size={16} className="text-red-600"/>
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                                <Paperclip size={14} className="text-slate-500"/>
                              </div>
                            )}
                            <div className="flex flex-col">
                              <span className="max-w-[140px] truncate font-semibold">{att.name}</span>
                              <span className="text-[9px] text-[color:var(--aii-muted)]">{isPdf ? "PDF" : isImg ? "Image" : "File"} · click to view</span>
                            </div>
                            <button onClick={(e) => { e.preventDefault(); removeAttachment("hotel", i, att.id); }}
                              className="ml-1 text-[color:var(--aii-muted)] hover:text-red-600 opacity-0 group-hover:opacity-100">×</button>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setHotels([...hotels, { name: "", price_per_night: 0, nights: 1, attachments: [] }])}
                      data-testid="cohort-add-hotel"
                      className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1"><Plus size={11}/> Add hotel</button>
            </div>
          </section>

          {/* Taxi + Guides — stacked vertically so the price input never
              collides with the guides column on the right (and typing in
              a fee can never remount the sibling section by accident). */}
          <section className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Taxi</div>
              <div className="space-y-1">
                {taxi.map((t, i) => (
                  <div key={`taxi-row-${i}`} className="space-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <select value={taxiReg.some(x => x.name === t.label) ? t.label : (t.label ? "__custom__" : "")}
                              onChange={e => {
                                const v = e.target.value;
                                if (v === "__addreg__") setTaxi(taxi.map((x, j) => j === i ? {...x, label: "", _promote: true} : x));
                                else if (v === "__custom__") setTaxi(taxi.map((x, j) => j === i ? {...x, label: "", _promote: false} : x));
                                else setTaxi(taxi.map((x, j) => j === i ? {...x, label: v, _promote: false} : x));
                              }}
                              data-testid={`cohort-taxi-select-${i}`}
                              className="flex-1 min-w-0 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                        <option value="">— Pick a taxi from Registry ({taxiReg.length}) —</option>
                        {taxiReg.map(r => <option key={r.id} value={r.name}>{r.name}{r.extra ? ` · ${r.extra}` : ""}</option>)}
                        <option value="__custom__">✎ Custom / new taxi…</option>
                        <option value="__addreg__">➕ Add new taxi to Registry…</option>
                      </select>
                      {!taxiReg.some(x => x.name === t.label) && t.label !== "" && (
                        <input value={t.label || ""}
                               onChange={e => setTaxi(taxi.map((x, j) => j === i ? {...x, label: e.target.value} : x))}
                               onBlur={(e) => { if (t._promote) { quickAddToRegistry("taxis", e.target.value); setTaxi(taxi.map((x, j) => j === i ? {...x, _promote: false} : x)); } }}
                               placeholder={t._promote ? "New taxi label (auto-saves to Registry on blur)" : "Custom taxi label"}
                               data-testid={`cohort-taxi-label-${i}`}
                               className={`flex-1 min-w-0 px-2 py-1 border rounded bg-white text-sm ${t._promote ? "border-brand" : "border-[color:var(--aii-border)]"}`}/>
                      )}
                      {numInput(t.amount, v => setTaxi(taxi.map((x, j) => j === i ? {...x, amount: v} : x)), "₹")}
                      <label title="Attach taxi bill / voucher"
                             className="text-[color:var(--aii-muted)] hover:text-brand cursor-pointer p-1 shrink-0"
                             data-testid={`cohort-taxi-attach-${i}`}>
                        <Paperclip size={14}/>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden"
                               onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadAttachmentTo("taxi", i, f); }}/>
                      </label>
                      <button type="button" onClick={() => setTaxi(taxi.filter((_, j) => j !== i))} className="text-[color:var(--aii-muted)] hover:text-red-600 shrink-0"><Trash2 size={12}/></button>
                    </div>
                    {(t.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 ml-2" data-testid={`cohort-taxi-atts-${i}`}>
                        {t.attachments.map(att => (
                          <span key={att.id} className="text-[11px] px-2 py-0.5 rounded border border-[color:var(--aii-border)] bg-[color:var(--aii-green-tint)] inline-flex items-center gap-1">
                            <FileText size={10}/>
                            <a href={att.public_url} target="_blank" rel="noreferrer" className="truncate max-w-[140px] hover:underline">{att.name}</a>
                            {att.public_url && <a href={att.public_url} target="_blank" rel="noreferrer" className="text-brand"><ExternalLink size={9}/></a>}
                            <button type="button" onClick={() => removeAttachment("taxi", i, att.id)} className="text-[color:var(--aii-muted)] hover:text-red-600 ml-1"><X size={9}/></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setTaxi([...taxi, { label: "", amount: 0 }])} data-testid="cohort-add-taxi" className="text-xs px-2 py-1 border border-brand text-brand rounded font-semibold flex items-center gap-1"><Plus size={11}/> Add taxi</button>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Guides</div>
              <div className="space-y-1">
                {guides.map((g, i) => (
                  <div key={`guide-row-${i}`} className="flex items-center gap-2 min-w-0">
                    <select value={guideReg.some(x => x.name === g.name) ? g.name : (g.name ? "__custom__" : "")}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === "__addreg__") setGuides(guides.map((x, j) => j === i ? {...x, name: "", _promote: true} : x));
                              else if (v === "__custom__") setGuides(guides.map((x, j) => j === i ? {...x, name: "", _promote: false} : x));
                              else setGuides(guides.map((x, j) => j === i ? {...x, name: v, _promote: false} : x));
                            }}
                            data-testid={`cohort-guide-select-${i}`}
                            className="flex-1 min-w-0 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm">
                      <option value="">— Pick a guide from Registry ({guideReg.length}) —</option>
                      {guideReg.map(r => <option key={r.id} value={r.name}>{r.name}{r.extra ? ` · ${r.extra}` : ""}</option>)}
                      <option value="__custom__">✎ Custom / new guide…</option>
                      <option value="__addreg__">➕ Add new guide to Registry…</option>
                    </select>
                    {!guideReg.some(x => x.name === g.name) && g.name !== "" && (
                      <input value={g.name || ""}
                             onChange={e => setGuides(guides.map((x, j) => j === i ? {...x, name: e.target.value} : x))}
                             onBlur={(e) => { if (g._promote) { quickAddToRegistry("guides", e.target.value); setGuides(guides.map((x, j) => j === i ? {...x, _promote: false} : x)); } }}
                             placeholder={g._promote ? "New guide name (auto-saves to Registry on blur)" : "Custom guide name"}
                             data-testid={`cohort-guide-name-${i}`}
                             className={`flex-1 min-w-0 px-2 py-1 border rounded bg-white text-sm ${g._promote ? "border-brand" : "border-[color:var(--aii-border)]"}`}/>
                    )}
                    {numInput(g.fee, v => setGuides(guides.map((x, j) => j === i ? {...x, fee: v} : x)), "₹")}
                    <button type="button" onClick={() => setGuides(guides.filter((_, j) => j !== i))} className="text-[color:var(--aii-muted)] hover:text-red-600 shrink-0"><Trash2 size={12}/></button>
                  </div>
                ))}
                <button type="button" onClick={() => setGuides([...guides, { name: "", fee: 0 }])} data-testid="cohort-add-guide" className="text-xs px-2 py-1 border border-brand text-brand rounded font-semibold flex items-center gap-1"><Plus size={11}/> Add guide</button>
              </div>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Water bottles / snacks (₹ per pax per day)</div>
            {numInput(waterSnacks, setWaterSnacks)}
            <span className="ml-2 text-xs text-[color:var(--aii-muted)]">= ₹{totals.waterTotal.toLocaleString()}</span>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Staff expenses</div>
            <div className="flex flex-wrap gap-3 items-center">
              <label className="text-xs flex items-center gap-1">Hotel room (₹) {numInput(staffHotelRoom, setStaffHotelRoom)}</label>
              <label className="text-xs flex items-center gap-1">Meals per day (₹) {numInput(staffMealsPerDay, setStaffMealsPerDay)}</label>
              <span className="text-xs text-[color:var(--aii-muted)]">= ₹{totals.staffTotal.toLocaleString()}</span>
            </div>
          </section>

          <section>
            <label className="text-[10px] uppercase tracking-widest text-brand font-bold">Notes</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
                      placeholder="Anything worth remembering for this cohort…"
                      className="mt-1 w-full px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
          </section>

          {/* Grand total */}
          <section className="border-t-2 border-brand pt-3">
            <div className="flex items-center justify-between" data-testid="cohort-grand-total">
              <div className="flex items-center gap-2">
                <Calculator size={16} className="text-brand"/>
                <span className="font-display font-semibold text-lg">Grand total</span>
              </div>
              <div className="text-2xl font-display font-bold text-brand">₹{totals.grand.toLocaleString()}</div>
            </div>
            <div className="text-[11px] text-[color:var(--aii-muted)] mt-1 text-right">
              meals ₹{totals.mealsTotal.toLocaleString()} · monuments ₹{totals.monumentsTotal.toLocaleString()} · hotels ₹{totals.hotelsTotal.toLocaleString()} · taxi ₹{totals.taxiTotal.toLocaleString()} · guides ₹{totals.guidesTotal.toLocaleString()} · water ₹{totals.waterTotal.toLocaleString()} · staff ₹{totals.staffTotal.toLocaleString()}
            </div>
          </section>

          {/* Below-threshold banner — pops the moment margin dips below the
              founder-configured floor so bad pricing is impossible to miss. */}
          {minMarginPct > 0 && revenue > 0 && totals.marginPct < minMarginPct && (
            <div className="rounded-md bg-red-50 border-2 border-red-400 p-2.5 text-sm text-red-800 font-semibold flex items-center gap-2"
                 data-testid="cohort-margin-alert">
              <TrendingDown size={14}/>
              Margin {totals.marginPct.toFixed(1)}% is below your {minMarginPct}% floor. Bump revenue or trim costs before saving.
            </div>
          )}

          {/* Profitability strip — shows revenue (sum of per-participant
              tour_price / sale_price), the grand cost, and the resulting
              margin. Colour flips red the moment cost outruns revenue so
              Founder spots underpriced cohorts at a glance. */}
          <section className={`rounded-md p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 ${totals.margin >= 0 ? "bg-[color:var(--aii-green-tint)]" : "bg-red-50 border border-red-200"}`}
                   data-testid="cohort-profitability">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold">Revenue</div>
              <div className="text-xl font-display font-bold">{fmt(revenue)}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">
                {(() => {
                  const bys = {};
                  (cohort.participants || []).forEach(p => {
                    const c = (p.revenue_currency || p.currency || "INR");
                    bys[c] = (bys[c] || 0) + (Number(p.revenue_raw ?? p.revenue) || 0);
                  });
                  const bits = Object.entries(bys).filter(([, v]) => v > 0)
                    .map(([c, v]) => `${c === "INR" ? "₹" : c === "USD" ? "$" : c === "EUR" ? "€" : c === "GBP" ? "£" : c + " "}${v.toLocaleString()}`);
                  return bits.length ? `${bits.join(" + ")} → ${displayCcy} · ${pax} pax` : `sum of tour prices · ${pax} pax`;
                })()}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold">Cost</div>
              <div className="text-xl font-display font-bold">{fmt(totals.grand)}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">grand total from above</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-text-2)] font-bold flex items-center gap-1">
                {totals.margin >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>} Margin
              </div>
              <div className={`text-xl font-display font-bold ${totals.margin >= 0 ? "text-brand" : "text-red-600"}`}>
                {fmt(totals.margin)}
              </div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">
                {revenue > 0 ? `${totals.marginPct.toFixed(1)}% margin` : "price cohort to reveal margin"}
              </div>
            </div>
          </section>

          <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[color:var(--aii-border)]">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
            <button type="button" onClick={() => setDiscardOpen(true)} disabled={discarding || saving} data-testid="cohort-discard"
                    className="text-sm px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 font-semibold flex items-center gap-1.5 disabled:opacity-50">
              <Trash2 size={13}/> Discard
            </button>
            <button type="button" onClick={() => setDupOpen(true)} data-testid="cohort-duplicate"
                    className="text-sm px-4 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
              <Copy size={13}/> Duplicate
            </button>
            <button type="button" onClick={downloadPdf} data-testid="cohort-pdf"
                    className="text-sm px-4 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
              Download PDF
            </button>
            <button type="button" onClick={share} disabled={sharing || saving} data-testid="cohort-share"
                    className="text-sm px-4 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5 disabled:opacity-50">
              <Send size={13}/> {sharing ? "Sharing…" : "Share cost sheet"}
            </button>
            <button type="button" onClick={save} disabled={saving} data-testid="cohort-save"
                    className="text-sm px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold disabled:opacity-50">
              {saving ? "Saving…" : "Save cost sheet"}
            </button>
          </div>
        </div>
      </div>

      {/* Discard-reason prompt — one-line reason logged into the Recycle
          Bin audit so a Founder later reading the bin knows WHY the
          sheet was ditched (cancelled tour, duplicate, replanned…). */}
      {discardOpen && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setDiscardOpen(false)}>
          <div className="bg-white max-w-md w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="cohort-discard-reason">
            <div className="p-5 border-b border-[color:var(--aii-border)]">
              <h3 className="font-display font-semibold text-lg text-red-700">Discard this cost sheet?</h3>
              <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Moved to the Recycle Bin. A Founder can restore it in one click.</p>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs text-[color:var(--aii-muted)] block">Why are you discarding? (optional, logged)</label>
              <div className="flex flex-wrap gap-1.5">
                {["Cancelled tour", "Duplicate", "Replanned", "Wrong data", "Test", "Other"].map(chip => (
                  <button key={chip} type="button" data-testid={`discard-chip-${chip.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={() => setDiscardReason(discardReason === chip ? "" : chip)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition ${discardReason === chip ? "bg-red-600 text-white border-red-600" : "border-[color:var(--aii-border)] hover:bg-red-50"}`}>
                    {chip}
                  </button>
                ))}
              </div>
              <input value={discardReason} onChange={(e) => setDiscardReason(e.target.value)}
                placeholder="Or type a custom reason…" data-testid="discard-reason-input"
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
            </div>
            <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
              <button onClick={() => setDiscardOpen(false)} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-slate-50">Cancel</button>
              <button data-testid="discard-confirm" onClick={discard} disabled={discarding}
                className="text-sm px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50">
                <Trash2 size={13}/> {discarding ? "Discarding…" : "Discard"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pull-from-bookings picker */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[75] bg-black/50 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Pick from bookings</div>
                <div className="font-display font-semibold text-lg">Matching weekend bookings</div>
                <div className="text-xs text-[color:var(--aii-muted)]">Click any participant to add to the tourist roster.</div>
              </div>
              <button onClick={() => setPickerOpen(false)} className="p-2 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-2" data-testid="cohort-picker-list">
              {pickerRows === null && <div className="text-sm text-[color:var(--aii-muted)]">Loading…</div>}
              {pickerRows?.length === 0 && <div className="text-sm text-[color:var(--aii-muted)]">No matching bookings — every candidate is already in the roster.</div>}
              {pickerRows?.map(r => (
                <button key={r.id} type="button" onClick={() => addFromPicker(r)}
                        data-testid={`cohort-picker-row-${r.id}`}
                        className="w-full text-left border border-[color:var(--aii-border)] rounded-md p-3 hover:border-brand hover:bg-[color:var(--aii-green-tint)] transition">
                  <div className="font-semibold text-sm">{r.customer || "—"}{r.gender && <span className="ml-2 text-[10px] uppercase text-[color:var(--aii-muted)]">· {r.gender}</span>}</div>
                  <div className="text-[11px] text-[color:var(--aii-muted)]">
                    {r.partner ? `via ${r.partner} · ` : ""}{r.destinations} · {r.start_date}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Duplicate cost-sheet flow */}
      {dupOpen && (
        <div className="fixed inset-0 z-[75] bg-black/50 flex items-center justify-center p-4" onClick={() => setDupOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[color:var(--aii-border)]">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Duplicate cost sheet</div>
              <div className="font-display font-semibold text-lg">Rebook this weekend template</div>
              <div className="text-xs text-[color:var(--aii-muted)]">Copies every line item — meals, monuments, hotels, taxi, guides, staff — to the new date/destination.</div>
            </div>
            <div className="p-4 space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-widest text-[color:var(--aii-text-2)]">New start date
                <input type="date" value={dupDate} onChange={e => setDupDate(e.target.value)}
                       data-testid="cohort-dup-date"
                       className="mt-1 w-full px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"/>
              </label>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[color:var(--aii-text-2)]">Destination
                <input value={dupDest} onChange={e => setDupDest(e.target.value)}
                       data-testid="cohort-dup-dest"
                       className="mt-1 w-full px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"
                       placeholder="e.g. Ranthambore"/>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setDupOpen(false)} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded">Cancel</button>
                <button type="button" onClick={duplicate} data-testid="cohort-dup-confirm"
                        className="text-sm px-3 py-1.5 bg-brand text-white rounded font-semibold">Duplicate</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="cohort-lightbox">
          <div className="relative max-w-6xl max-h-[92vh] w-full flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white mb-2">
              <div className="font-semibold truncate flex items-center gap-2"><FileText size={14}/> {lightbox.name}</div>
              <div className="flex items-center gap-2">
                <a href={lightbox.url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 border border-white/30 rounded hover:bg-white/10 flex items-center gap-1"><ExternalLink size={11}/> Open new tab</a>
                <button onClick={() => setLightbox(null)} className="text-xs px-2 py-1 border border-white/30 rounded hover:bg-white/10 flex items-center gap-1"><X size={11}/> Close</button>
              </div>
            </div>
            <div className="flex-1 bg-white rounded-lg overflow-hidden flex items-center justify-center">
              {lightbox.kind === "image" ? (
                <img src={lightbox.url} alt={lightbox.name} className="max-w-full max-h-[85vh] object-contain"/>
              ) : (
                <iframe src={lightbox.url} title={lightbox.name} className="w-full h-[85vh] border-0"/>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
